/**
 * Zod Schemas for the etl_integrations digital dispatch API
 *
 * The digital dispatch framework serves every non-GMR resource integration
 * (currently TowPro) through a shared set of endpoints. Responses are
 * camelCase and are returned bare, without the GMR-style status/result
 * envelope. These schemas validate responses at runtime and derive
 * TypeScript types via z.infer.
 */
import { z } from 'zod';

export const DIGITAL_DISPATCH_INTEGRATIONS = {
  TOWPRO: 'towpro',
  NORFOLK_SOUTHERN: 'norfolk-southern',
} as const;

export const DigitalDispatchQueueStateSchema = z.enum(['open', 'closed']);
export type DigitalDispatchQueueState = z.infer<
  typeof DigitalDispatchQueueStateSchema
>;

const TowProAddressInputLocationSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
});

const TowProAddressInputSchema = z.object({
  address1: z.string().nullish(),
  country: z.string().nullish(),
  state: z.string().nullish(),
  zip: z.string().nullish(),
  phone: z.string().nullish(),
  location: TowProAddressInputLocationSchema,
});
export type TowProAddressInput = z.infer<typeof TowProAddressInputSchema>;

const TowProVehicleOwnerDataSchema = z.object({
  apt: z.string().nullish(),
  street: z.string().nullish(),
  city: z.string().nullish(),
  zipCode: z.string().nullish(),
  state: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  phone: z.string().nullish(),
});
export type TowProVehicleOwnerData = z.infer<
  typeof TowProVehicleOwnerDataSchema
>;

// Request body for POST/PUT /api/towpro/requests. Mirrors the backend
// TowProDispatchRequest model (camelCase aliases).
export const TowProDispatchRequestSchema = z.object({
  regionId: z.number(),
  addressInput: TowProAddressInputSchema,
  // Reasons are per-municipality and come back from the municipalities
  // lookup, so they are sent as IDs rather than as text.
  towReasonId: z.number(),
  // TowPro requires these when the chosen reason has sub-reasons; reasons
  // without any send an empty list.
  towSubreasonIds: z.array(z.number()).default([]),
  // The tow rotation always determines the assigned tow company, so the
  // backend requires this to be literally true.
  sentToRotation: z.literal(true),
  notes: z.string().nullish(),
  licensePlateNumber: z.string().nullish(),
  licensePlateState: z.string().nullish(),
  make: z.string().nullish(),
  model: z.string().nullish(),
  color: z.string().nullish(),
  vehicleYear: z.string().nullish(),
  incidentNumber: z.string().nullish(),
  agencyId: z.number().nullish(),
  vin: z.string().nullish(),
  officerName: z.string().nullish(),
  badgeNumber: z.string().nullish(),
  vehicleOwnerData: TowProVehicleOwnerDataSchema.nullish(),
});
export type TowProDispatchRequest = z.infer<typeof TowProDispatchRequestSchema>;

// The request data echoed back on responses also carries the backend-computed
// dispatch typeId, which the frontend never sends.
const TowProDispatchRequestDataSchema = TowProDispatchRequestSchema.extend({
  typeId: z.number().optional(),
});

/**
 * Status data embedded inside the TowProStatusResponseSchema envelope.
 */
const TowProStatusData = z.object({
  status: z.string(),
  display: z.string().optional(),
  timestamp: z.string(),
});

/**
 * A single ingested TowPro status. `createdAt` is when we ingested the status;
 * the nested `timestamp` is when TowPro says it happened, which is what the
 * timeline orders and displays.
 */
const TowProStatusResponseSchema = z.object({
  id: z.number(),
  requestId: z.number(),
  createdAt: z.string(),
  integration: z.literal(DIGITAL_DISPATCH_INTEGRATIONS.TOWPRO),
  status: TowProStatusData,
});
export type TowProStatusResponse = z.infer<typeof TowProStatusResponseSchema>;

/**
 * Information about the assigned tow company.
 */
const TowProAssignedTowCompany = z.object({
  name: z.string().nullable(),
  phoneNumber: z.string().nullable(),
});

// Response shape shared by the create/update/list endpoints for TowPro
// requests (the digital dispatch DispatchRequestResponse model).
export const TowProRequestResponseSchema = z.object({
  id: z.number(),
  createdAt: z.string(),
  callerId: z.string().nullish(),
  integration: z.literal(DIGITAL_DISPATCH_INTEGRATIONS.TOWPRO),
  queueState: DigitalDispatchQueueStateSchema,
  request: z.object({
    data: TowProDispatchRequestDataSchema,
    // Set by the backend once the cancel endpoint has forwarded the
    // cancellation to TowPro; defaulted for rows that predate the field.
    cancelled: z.boolean().default(false),
    // Set by the backend once TowPro reports the tow as completed; defaulted
    // for rows that predate the field.
    complete: z.boolean().default(false),
    // Set by the backend once we ingest a status with the assigned tow company
    assignedCompany: TowProAssignedTowCompany.nullable(),
  }),
  statuses: z.array(TowProStatusResponseSchema),
});
export type TowProRequestResponse = z.infer<typeof TowProRequestResponseSchema>;

/**
 * GET /api/digital-dispatch/requests returns every integration's requests
 * for the account as a discriminated union keyed on `integration`. Unknown
 * integrations are skipped so this consumer keeps working when new
 * integrations register; malformed TowPro rows fail the parse loudly.
 */
const DigitalDispatchListItemProbeSchema = z
  .object({ integration: z.string() })
  .passthrough();

/**
 * One unparsed row of GET /api/digital-dispatch/requests. Only the fields
 * the shared cache itself touches are typed; each integration parses the
 * rest with its own schema.
 */
export type DigitalDispatchListRow = Record<string, unknown> & {
  id?: unknown;
  integration?: unknown;
  queueState?: unknown;
};

/** The shared list as the framework returns it: an array of rows. */
export function parseDigitalDispatchListRows(
  response: unknown,
): DigitalDispatchListRow[] {
  return z
    .array(z.record(z.unknown()))
    .parse(response) as DigitalDispatchListRow[];
}

export function parseTowProRequests(
  response: unknown,
): TowProRequestResponse[] {
  const items = z.array(z.unknown()).parse(response);

  const towProRequests: TowProRequestResponse[] = [];
  for (const item of items) {
    const probe = DigitalDispatchListItemProbeSchema.safeParse(item);
    if (
      !probe.success ||
      probe.data.integration !== DIGITAL_DISPATCH_INTEGRATIONS.TOWPRO
    ) {
      continue;
    }
    towProRequests.push(TowProRequestResponseSchema.parse(item));
  }

  return towProRequests;
}

// Body for POST /api/towpro/requests/:id/cancel. moveCompanyTo tells TowPro
// where the released vendor re-enters the rotation list.
export const TowProCancelRequestSchema = z.object({
  reason: z.string(),
  moveCompanyTo: z.enum(['TOP', 'BOTTOM']),
});
export type TowProCancelRequest = z.infer<typeof TowProCancelRequestSchema>;

// GET /api/towpro/municipalities response. Regions are pre-filtered by the
// backend to active regions of towing-dispatch submunicipalities.
const TowProRegionSchema = z.object({
  id: z.number(),
  uuid: z.string(),
  name: z.string(),
  active: z.boolean(),
});
export type TowProRegion = z.infer<typeof TowProRegionSchema>;

/** A law enforcement agency that can be named on a tow request. */
const TowProAgencySchema = z.object({
  id: z.number(),
  name: z.string(),
});
export type TowProAgency = z.infer<typeof TowProAgencySchema>;

const TowProSubMunicipalitySchema = z.object({
  id: z.number(),
  regions: z.array(TowProRegionSchema),
  agencies: z.array(TowProAgencySchema).default([]),
});

/**
 * A sub-reason narrowing one of the municipality's tow reasons. Only some
 * reasons have them, and when a reason does TowPro requires at least one.
 */
const TowProSubReasonSchema = z.object({
  id: z.number(),
  name: z.string(),
});
export type TowProSubReason = z.infer<typeof TowProSubReasonSchema>;

/**
 * A tow reason offered by the municipality. The set is configured per
 * municipality in TowPro rather than fixed, which is why it is fetched
 * alongside the regions instead of hardcoded here.
 */
const TowProReasonSchema = z.object({
  id: z.number(),
  name: z.string(),
  subReasons: z.array(TowProSubReasonSchema).default([]),
});
export type TowProReason = z.infer<typeof TowProReasonSchema>;

export const TowProMunicipalitiesResponseSchema = z.object({
  name: z.string(),
  subMunicipalities: z.array(TowProSubMunicipalitySchema),
  reasons: z.array(TowProReasonSchema).default([]),
});
export type TowProMunicipalitiesResponse = z.infer<
  typeof TowProMunicipalitiesResponseSchema
>;

// GET /api/towpro/vin-lookup/:vin response. Field casing comes from the
// upstream vehicle data provider.
const TowProVinLookupResultSchema = z.object({
  Make: z.string(),
  Model: z.string(),
  ModelYear: z.string(),
  VIN: z.string(),
  VehicleType: z.string(),
});
export type TowProVinLookupResult = z.infer<typeof TowProVinLookupResultSchema>;

export const TowProVinLookupResponseSchema = z.object({
  Count: z.number(),
  Results: z.array(TowProVinLookupResultSchema),
});
export type TowProVinLookupResponse = z.infer<
  typeof TowProVinLookupResponseSchema
>;
